import React from "react";
import swal from "sweetalert";
import { shouldShowOpeningBalanceUnlock } from "./openingBalanceDate";
import { isOwnerRole } from "./ownerRole";
import { isPlatformSuperAdmin } from "./platformAuth";
import "./OpeningBalanceUnlockButton.css";

export const OPENING_BALANCE_UNLOCK_DENIED_MESSAGE =
  "Only Owner or Super Admin can unlock opening balance editing.";

export const OPENING_BALANCE_LOCK_DENIED_MESSAGE =
  "Only Owner or Super Admin can lock opening balance editing.";

const authHeaders = (token) => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${token}`,
  Accept: "application/json",
});

/**
 * Pump Owner or platform Super Admin may unlock opening-balance edits after ops start.
 * Managers may edit once the pump is unlocked.
 */
export const canUnlockOpeningBalanceRole = (roleNameOrSlug) => {
  if (isPlatformSuperAdmin(roleNameOrSlug)) {
    return true;
  }

  return isOwnerRole(roleNameOrSlug);
};

/** Fetch pump unlock status: who may activate, and whether editing is currently allowed. */
export async function fetchOpeningBalanceUnlockStatus(apiUrl, token) {
  if (!token) {
    return { eligible: false, unlocked: false };
  }

  const response = await fetch(`${apiUrl}/opening_balance_unlock_eligible`, {
    method: "get",
    headers: authHeaders(token),
  });

  if (!response.ok) {
    return { eligible: false, unlocked: false };
  }

  const data = await response.json();
  return {
    eligible: Boolean(data?.eligible),
    unlocked: Boolean(data?.unlocked),
  };
}

/** @deprecated Use fetchOpeningBalanceUnlockStatus */
export async function fetchCanUnlockOpeningBalance(apiUrl, token) {
  const status = await fetchOpeningBalanceUnlockStatus(apiUrl, token);
  return status.eligible;
}

/** Persist pump-wide unlock so Owner, Manager, and Super Admin can edit. */
export async function activateOpeningBalanceUnlock(apiUrl, token) {
  if (!token) {
    throw new Error("Authentication required");
  }

  const response = await fetch(`${apiUrl}/opening_balance_unlock`, {
    method: "post",
    headers: authHeaders(token),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.message || "Unable to unlock opening balance editing");
  }

  return data;
}

/** Lock pump-wide opening balance editing again. */
export async function deactivateOpeningBalanceUnlock(apiUrl, token) {
  if (!token) {
    throw new Error("Authentication required");
  }

  const response = await fetch(`${apiUrl}/opening_balance_lock`, {
    method: "post",
    headers: authHeaders(token),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.message || "Unable to lock opening balance editing");
  }

  return data;
}

/**
 * Confirm and toggle pump-wide opening-balance edit lock.
 * Used by all opening-balance screens so unlock and lock stay consistent.
 */
export function toggleOpeningBalanceEditUnlock({
  apiUrl,
  canUnlock,
  unlocked,
  onUnlockedChange,
  onAfterChange,
}) {
  const locking = Boolean(unlocked);

  if (!canUnlock) {
    swal(
      "",
      locking
        ? OPENING_BALANCE_LOCK_DENIED_MESSAGE
        : OPENING_BALANCE_UNLOCK_DENIED_MESSAGE,
      "warning"
    );
    return;
  }

  swal({
    title: locking
      ? "Lock opening balance edit?"
      : "Enable opening balance edit?",
    text: locking
      ? "Lock opening balance editing for this pump? Opening balance fields will become read-only again."
      : "Unlock opening balance editing for this pump? Managers will also be able to edit.",
    icon: "warning",
    buttons: true,
    dangerMode: true,
  }).then((confirm) => {
    if (!confirm) return;

    const token = localStorage.getItem("AUTH_TOKEN") || "";
    const action = locking
      ? deactivateOpeningBalanceUnlock(apiUrl, token)
      : activateOpeningBalanceUnlock(apiUrl, token);

    action
      .then(() => {
        const nextUnlocked = !locking;
        onUnlockedChange?.(nextUnlocked);
        onAfterChange?.(nextUnlocked);
        swal(
          "",
          locking
            ? "Opening balance editing locked for this pump."
            : "Opening balance editing unlocked. Managers can now edit as well.",
          "success"
        );
      })
      .catch((error) => swal("", error.message, "error"));
  });
}

const LockIcon = ({ unlocked }) =>
  unlocked ? (
    <svg
      className="ob-unlock-btn__icon"
      viewBox="0 0 24 24"
      width="16"
      height="16"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="currentColor"
        d="M12 17a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm6-7h-1V7a5 5 0 0 0-9.9-1h2.1A3 3 0 0 1 15 7v3H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2z"
      />
    </svg>
  ) : (
    <svg
      className="ob-unlock-btn__icon"
      viewBox="0 0 24 24"
      width="16"
      height="16"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="currentColor"
        d="M18 10h-1V7a5 5 0 0 0-10 0v3H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2zM9 7a3 3 0 0 1 6 0v3H9V7zm3 12a2 2 0 1 1 0-4 2 2 0 0 1 0 4z"
      />
    </svg>
  );

/**
 * Visible unlock/lock control for after-ops opening-balance edits.
 * Uses a dedicated class so modal-header close-button CSS cannot flatten it to text.
 */
export const OpeningBalanceUnlockButton = ({
  statOperation,
  unlocked = false,
  canUnlock = false,
  onUnlock,
  className = "",
}) => {
  if (!shouldShowOpeningBalanceUnlock(statOperation)) {
    return null;
  }

  const isDisabled = !canUnlock;

  return (
    <button
      type="button"
      className={`ob-unlock-btn ${unlocked ? "ob-unlock-btn--unlocked" : ""} ${className}`.trim()}
      onClick={onUnlock}
      disabled={isDisabled}
      title={
        unlocked
          ? canUnlock
            ? "Lock opening balance editing for this pump"
            : "Opening balance editing is unlocked for this pump"
          : canUnlock
            ? "Unlock opening balance editing for this pump"
            : OPENING_BALANCE_UNLOCK_DENIED_MESSAGE
      }
    >
      <LockIcon unlocked={unlocked} />
      <span className="ob-unlock-btn__label">
        {unlocked
          ? canUnlock
            ? "Lock Opening Balance Edit"
            : "Opening Balance Unlocked"
          : "Unlock Opening Balance Edit"}
      </span>
    </button>
  );
};

export default OpeningBalanceUnlockButton;
